'use client';

import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { initTossWidgets, sanitizeOrderName, ANONYMOUS } from '@/lib/toss';

interface Props {
  amount: number;
  orderId: string;
  orderName: string;
  customerPhone?: string;
  onReady?: () => void;
}

export interface TossPaymentWidgetRef {
  requestPayment: () => Promise<void>;
}

const TossPaymentWidget = forwardRef<TossPaymentWidgetRef, Props>(
  ({ amount, orderId, orderName, customerPhone, onReady }, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const widgetsRef = useRef<any>(null);
    const [isReady, setIsReady] = useState(false);
    const [initError, setInitError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
      let mounted = true;
      setInitError(null);

      async function init() {
        try {
          console.log('[TossWidget] Step 1: initTossWidgets, amount=', amount);
          const widgets = await initTossWidgets(ANONYMOUS);
          if (!mounted) return;

          console.log('[TossWidget] Step 2: setAmount', amount);
          await widgets.setAmount({ value: amount, currency: 'KRW' });

          console.log('[TossWidget] Step 3: renderPaymentMethods');
          await widgets.renderPaymentMethods({
            selector: '#payment-methods',
            variantKey: 'DEFAULT',
          });

          console.log('[TossWidget] Step 4: renderAgreement');
          await widgets.renderAgreement({
            selector: '#payment-agreement',
          });

          console.log('[TossWidget] Done - widget ready');
          widgetsRef.current = widgets;
          setIsReady(true);
          onReady?.();
        } catch (error: unknown) {
          console.error('[TossWidget] 초기화 실패:', error);
          console.error('[TossWidget] error keys:', error && typeof error === 'object' ? Object.keys(error) : 'N/A');
          console.error('[TossWidget] JSON:', JSON.stringify(error, null, 2));
          if (mounted) {
            const e = error as { message?: string; code?: string };
            const msg = e?.code
              ? `[${e.code}] ${e.message}`
              : e?.message || JSON.stringify(error);
            setInitError(msg);
          }
        }
      }

      init();
      return () => {
        mounted = false;
      };
    }, [retryCount]);

    // Update amount when it changes after init
    useEffect(() => {
      if (widgetsRef.current && isReady) {
        widgetsRef.current.setAmount({ value: amount, currency: 'KRW' });
      }
    }, [amount, isReady]);

    useImperativeHandle(ref, () => ({
      requestPayment: async () => {
        if (!widgetsRef.current) throw new Error('결제위젯이 초기화되지 않았습니다.');

        await widgetsRef.current.requestPayment({
          orderId,
          orderName: sanitizeOrderName(orderName),
          customerMobilePhone: customerPhone?.replace(/-/g, '') || undefined,
          successUrl: `${window.location.origin}/payment/success`,
          failUrl: `${window.location.origin}/payment/fail`,
        });
      },
    }));

    return (
      <div>
        <div id="payment-methods" />
        <div id="payment-agreement" />

        {!isReady && !initError && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-[#1A3A5C] border-t-transparent rounded-full" />
            <span className="ml-2 text-sm text-gray-500">결제 수단을 불러오는 중...</span>
          </div>
        )}

        {initError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-sm text-red-700 mb-3">{initError}</p>
            <button
              type="button"
              onClick={() => {
                setIsReady(false);
                setInitError(null);
                widgetsRef.current = null;
                setRetryCount((c) => c + 1);
              }}
              className="px-4 py-2 bg-[#1A3A5C] text-white rounded-lg text-sm font-semibold"
            >
              다시 시도
            </button>
          </div>
        )}
      </div>
    );
  }
);

TossPaymentWidget.displayName = 'TossPaymentWidget';

export default TossPaymentWidget;
